import { Modal } from '@benzinga/core-ui';
import React from 'react';
import styled from 'styled-components';
import { ConfigSelectorOption } from '../ConfigSelector';
import { Select } from 'antd';
import { Close } from '@benzinga/themed-icons';

interface DefaultConfigSelectionDropdownProps {
  configs: ConfigSelectorOption[];
  handleDeleteConfig: (id: string) => void;
  defaultSelectedConfigUUID: string | undefined;
  handleDefaultConfigSetter: (id: string) => void;
}

export const DefaultConfigSelectionDropdown: React.FC<DefaultConfigSelectionDropdownProps> = props => {
  const [deleteModalState, setDeleteModalState] = React.useState<{ show: boolean; id: string }>({
    id: '',
    show: false,
  });
  const findConfig = React.useCallback((id: string) => props.configs.find(config => config.id === id), [props.configs]);
  const [selectedConfig, setSelectedConfig] = React.useState(findConfig(props.defaultSelectedConfigUUID ?? ''));
  const configItems = React.useMemo(
    () =>
      props.configs.map(config => ({
        customLabel: (
          <MenuItem>
            <span>{config.displayText}</span>
            <Close
              onClick={() =>
                setDeleteModalState({
                  id: config.id,
                  show: true,
                })
              }
            />
          </MenuItem>
        ),
        key: config.id,
        label: config.displayText,
        value: config.id,
      })),
    [props.configs],
  );
  const handleConfigSelection = React.useCallback(
    e => {
      const configSetter = props.handleDefaultConfigSetter;
      setSelectedConfig(findConfig(e));
      configSetter(e);
    },
    [findConfig, props.handleDefaultConfigSetter],
  );

  const handleConfigClear = React.useCallback(() => {
    const configSetter = props.handleDefaultConfigSetter;
    setSelectedConfig(undefined);
    configSetter('');
  }, [props.handleDefaultConfigSetter]);

  const handleCloseModalAndDeleteConfig = React.useCallback(
    (id: string) => {
      setDeleteModalState(prevState => ({ ...prevState, show: false }));
      const closeModalAndDeleteConfig = props.handleDeleteConfig;
      closeModalAndDeleteConfig(id);
    },
    [props.handleDeleteConfig],
  );

  return (
    <>
      <Configs
        allowClear
        onClear={handleConfigClear}
        onSelect={handleConfigSelection}
        optionFilterProp="label"
        optionRender={option => option.data.customLabel}
        options={configItems}
        placeholder="Set a default config"
        showSearch
        value={selectedConfig?.id}
      />

      <Modal
        disableFullScreenButton
        onClose={() =>
          setDeleteModalState(prevState => ({
            ...prevState,
            show: false,
          }))
        }
        size={{ alert: true }}
        title="Delete Configuration?"
        visible={deleteModalState.show}
      >
        <span className="Modal-content">
          <span>
            Are you sure you want to delete <b>{selectedConfig?.displayText}</b>?
          </span>
        </span>
        <div className="Modal-proceed">
          <button
            className="Button Button--text"
            onClick={() =>
              setDeleteModalState(prevState => ({
                ...prevState,
                show: false,
              }))
            }
          >
            Cancel
          </button>
          <button
            className="Button Button--primary"
            onClick={() => handleCloseModalAndDeleteConfig(deleteModalState.id)}
          >
            Delete
          </button>
        </div>
      </Modal>
    </>
  );
};

const Configs = styled(Select)`
  background: ${props => props.theme.colors.brand};
  width: 20%;
  max-height: 50%;
`;

const MenuItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
